import React, { Fragment, useId, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import sales from '../../Sales';
import { useAdmin } from '../adminContext';
import { amountInput, formatEuro, formatMonth, monthKey, parseAmount, plural } from '../format';
import { useAdminData } from '../store';
import { Button, EntryCard, Field, FormError, ListState, MoneyInput, useFreshIds } from '../ui';

const monthNames = Array.from({ length: 12 }, (_, index) =>
  dayjs().locale('nl').month(index).format('MMMM'),
);

const TargetForm = ({ target, takenMonths, onDone }) => {
  const isNew = !target;
  const { notify, confirm, changed } = useAdmin();
  const id = useId();

  const [start] = useState(() => {
    if (target?.date) return dayjs(target.date);
    let month = dayjs().startOf('month');
    while (takenMonths.includes(month.format('YYYY-MM'))) month = month.add(1, 'month');
    return month;
  });

  const [form, setForm] = useState({
    month: start.month(),
    year: start.year(),
    amount: amountInput(target?.amount),
    text: target?.text || '',
  });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const thisYear = dayjs().year();
  const years = [...new Set([thisYear - 1, thisYear, thisYear + 1, start.year()])].sort();
  const set = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));
  const key = `${form.year}-${String(form.month + 1).padStart(2, '0')}`;

  const submit = async () => {
    const amount = parseAmount(form.amount);
    if (amount === null || Number.isNaN(amount) || amount <= 0) {
      setProblem('Vul een geldig targetbedrag in.');
      return;
    }
    if (takenMonths.includes(key)) {
      setProblem(`Voor ${formatMonth(`${key}-01`)} staat al een target. Klik het aan in de lijst om het te wijzigen.`);
      return;
    }

    const date = target && monthKey(target.date) === key ? target.date : `${key}-01`;
    const body = { date, amount, text: form.text.trim() };

    setBusy(true);
    setProblem('');
    try {
      if (isNew) {
        await sales.post('bonuses', body);
        notify(`Target voor ${formatMonth(`${key}-01`)} ingesteld`);
        onDone();
      } else {
        await sales.put('bonuses', { bonus_id: target.bonus_id, ...body });
        notify('Target opgeslagen');
        onDone();
      }
      changed();
    } catch (error) {
      console.warn(error);
      setProblem('Opslaan is mislukt. Probeer het opnieuw.');
      setBusy(false);
    }
  };

  const remove = async () => {
    const confirmed = await confirm({
      title: 'Target verwijderen?',
      text: `Het target voor ${formatMonth(target.date)} wordt verwijderd. De TV laat die maand dan "target onbekend" zien.`,
    });
    if (!confirmed) return;
    setBusy(true);
    try {
      await sales.delete(`bonuses?bonus_id=${target.bonus_id}`);
      notify('Target verwijderd');
      changed();
      onDone();
    } catch (error) {
      console.warn(error);
      notify('Verwijderen is mislukt', 'error');
      setBusy(false);
    }
  };

  return (
    <EntryCard
      title={isNew ? 'Nieuw team target' : 'Target bewerken'}
      inline={!isNew}
      onSubmit={submit}
      onCancel={onDone}
      busy={busy}
      actions={
        <>
          {!isNew && (
            <Button variant="danger-text" icon={Trash2} onClick={remove} disabled={busy}>
              Verwijderen
            </Button>
          )}
          <span className="adm-grow" />
          {!isNew && (
            <Button variant="ghost" onClick={onDone} disabled={busy}>
              Annuleren
            </Button>
          )}
          <Button type="submit" icon={isNew ? Plus : undefined} disabled={busy}>
            {busy ? 'Bezig...' : isNew ? 'Toevoegen' : 'Opslaan'}
          </Button>
        </>
      }
    >
      <div className="adm-target-grid">
        <Field label="Maand" htmlFor={`${id}-month`}>
          <select
            id={`${id}-month`}
            className="adm-input adm-capitalize"
            value={form.month}
            onChange={(event) => set('month', Number(event.target.value))}
          >
            {monthNames.map((name, index) => (
              <option key={name} value={index}>
                {name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Jaar" htmlFor={`${id}-year`}>
          <select
            id={`${id}-year`}
            className="adm-input"
            value={form.year}
            onChange={(event) => set('year', Number(event.target.value))}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Target" htmlFor={`${id}-amount`}>
          <MoneyInput
            id={`${id}-amount`}
            value={form.amount}
            onChange={(event) => set('amount', event.target.value)}
            placeholder="85000"
          />
        </Field>
        <Field label="Uitje bij 100%" htmlFor={`${id}-text`}>
          <input
            id={`${id}-text`}
            className="adm-input"
            type="text"
            value={form.text}
            onChange={(event) => set('text', event.target.value)}
            placeholder="Bijvoorbeeld karten + BBQ"
          />
        </Field>
      </div>
      <FormError>{problem}</FormError>
    </EntryCard>
  );
};

const Targets = () => {
  const [editing, setEditing] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const { data, loading, error, reload } = useAdminData({ bonuses: 'bonuses' });
  const currentMonth = dayjs().format('YYYY-MM');

  const rows = useMemo(
    () =>
      data
        ? [...data.bonuses].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
        : [],
    [data],
  );

  const fresh = useFreshIds(rows, (row) => row.bonus_id);

  const monthsExcept = (bonusId) =>
    rows.filter((row) => row.bonus_id !== bonusId).map((row) => monthKey(row.date));

  const added = () => setFormKey((value) => value + 1);

  return (
    <>
      {data ? (
        <TargetForm key={formKey} takenMonths={monthsExcept(null)} onDone={added} />
      ) : null}
      <section className="adm-panel">
        <div className="adm-panel-head">
          <h2 className="adm-panel-title">Team targets</h2>
          {data && <span className="adm-count num">{plural(rows.length, 'maand', 'maanden')}</span>}
        </div>
        <ListState
          loading={loading && !data}
          error={error && !data}
          onRetry={reload}
          empty={rows.length === 0 && 'Nog geen team targets.'}
        >
          <div className="adm-rows">
            {rows.map((row) => {
              const open = editing === row.bonus_id;
              return (
                <Fragment key={row.bonus_id}>
                  <button
                    type="button"
                    className={`adm-row adm-row-target${open ? ' is-open' : ''}${fresh.has(row.bonus_id) ? ' is-fresh' : ''}`}
                    aria-expanded={open}
                    onClick={() => setEditing(open ? null : row.bonus_id)}
                  >
                    <span className="adm-row-main">
                      <b className="adm-capitalize">{formatMonth(row.date)}</b>
                      <small>{row.text || 'Nog geen uitje'}</small>
                    </span>
                    {monthKey(row.date) === currentMonth && <span className="adm-kind is-new">Deze maand</span>}
                    <span className="adm-row-amount num">{formatEuro(row.amount, { round: true })}</span>
                    <Pencil size={15} className="adm-row-edit" aria-label="Bewerken" />
                  </button>
                  {open && (
                    <div className="adm-row-editor">
                      <TargetForm target={row} takenMonths={monthsExcept(row.bonus_id)} onDone={() => setEditing(null)} />
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        </ListState>
      </section>
    </>
  );
};

export default Targets;
